import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Monitor, 
  CheckCircle2,
  Globe,
  Smartphone,
  Code,
  Palette,
  Gauge,
  ShoppingCart,
  Layout,
  Layers,
  Zap,
  Eye,
  MousePointer,
  FileCode,
  Database,
  Shield,
  Clock,
  Server,
  CloudCog,
  RefreshCw,
  Activity
} from 'lucide-react';

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div data-testid="services-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-40 pb-20 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] to-[#050505]" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="overline">Our Services</span>
            <h1 className="heading-hero text-[#F5F5F7] mt-4 mb-6">
              Web Design &
              <br />
              <span className="text-gradient-gold">Development</span>
            </h1>
            <p className="body-large">
              Premium web solutions crafted with precision. From custom websites to 
              e-commerce platforms, we build digital experiences that perform.
            </p>
          </div>
        </div>
      </section>

      {/* Custom Website Development */}
      <section 
        id="custom-websites"
        data-testid="service-section-custom-websites"
        className="section-padding bg-[#050505] relative"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-6xl font-bold text-[#D4AF37]/20">01</span>
                <Monitor className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h2 className="heading-section text-[#F5F5F7] mb-4">Custom Website Development</h2>
              <p className="text-[#D4AF37] text-lg font-medium mb-6">
                Bespoke websites built for your unique brand and audience.
              </p>
              <p className="body-large mb-8">
                We design and develop custom websites from scratch, tailored to your specific business needs. 
                No templates, no shortcuts. Every element is crafted to deliver an exceptional user experience 
                and drive conversions.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                <div className="flex items-center gap-3">
                  <Code className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Clean, custom code</span>
                </div>
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Fully responsive design</span>
                </div>
                <div className="flex items-center gap-3">
                  <Gauge className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Optimized performance</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Secure & reliable</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Unique design that stands out from competitors</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Conversion-optimized user journeys</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Fast loading times across all devices</span>
                </div>
              </div>

              <div className="accent-border-left bg-[#111111] p-6">
                <p className="text-[#71717A] text-sm uppercase tracking-wider mb-2">What's Included</p>
                <ul className="text-[#A1A1AA] text-sm space-y-2">
                  <li>• Custom design & development</li>
                  <li>• Mobile responsive across all devices</li>
                  <li>• Content management system (CMS)</li>
                  <li>• Contact forms & integrations</li>
                  <li>• Browser compatibility testing</li>
                  <li>• 30-day post-launch support</li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              <div className="aspect-video bg-[#111111] border border-white/10 relative overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1547658719-da2b51169166?q=80&w=2564&auto=format&fit=crop"
                  alt="Custom Website Development"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-60" />
              </div>
              
              <div className="bg-[#111111] border border-white/10 p-6">
                <h4 className="text-[#F5F5F7] font-semibold mb-4">Technologies We Use:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Code className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">React / Next.js</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Palette className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Tailwind CSS</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Database className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">WordPress / Webflow</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Globe className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Modern Hosting</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Landing Page Development */}
      <section 
        id="landing-pages"
        data-testid="service-section-landing-pages"
        className="section-padding bg-[#0a0a0a] relative"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-6xl font-bold text-[#D4AF37]/20">02</span>
                <Layout className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h2 className="heading-section text-[#F5F5F7] mb-4">Landing Page Development</h2>
              <p className="text-[#D4AF37] text-lg font-medium mb-6">
                High-converting pages engineered for results.
              </p>
              <p className="body-large mb-8">
                We create focused, conversion-optimized landing pages designed to turn visitors 
                into customers. Every element is strategically placed to guide users toward 
                your desired action.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                <div className="flex items-center gap-3">
                  <MousePointer className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Clear call-to-actions</span>
                </div>
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Attention-grabbing design</span>
                </div>
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Lightning-fast loading</span>
                </div>
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Mobile-optimized</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Conversion-focused design principles</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">A/B testing ready structure</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Analytics integration included</span>
                </div>
              </div>

              <div className="accent-border-left bg-[#111111] p-6">
                <p className="text-[#71717A] text-sm uppercase tracking-wider mb-2">What's Included</p>
                <ul className="text-[#A1A1AA] text-sm space-y-2">
                  <li>• Custom landing page design</li>
                  <li>• Responsive development</li>
                  <li>• Form integration & lead capture</li>
                  <li>• Speed optimization</li>
                  <li>• Analytics setup</li>
                  <li>• 2 rounds of revisions</li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              <div className="aspect-video bg-[#111111] border border-white/10 relative overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1634084462412-b54873c0a56d?q=80&w=2340&auto=format&fit=crop"
                  alt="Landing Page Development"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-60" />
              </div>
              
              <div className="bg-[#111111] border border-white/10 p-6">
                <h4 className="text-[#F5F5F7] font-semibold mb-4">Landing Page Types:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Layout className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Product Launch</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <FileCode className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Service Pages</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <MousePointer className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Sign-up Pages</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Eye className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Event Pages</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* E-Commerce Development */}
      <section 
        id="ecommerce"
        data-testid="service-section-ecommerce"
        className="section-padding bg-[#050505] relative"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/5 to-transparent" />
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-6xl font-bold text-[#D4AF37]/20">03</span>
                <ShoppingCart className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h2 className="heading-section text-[#F5F5F7] mb-4">E-Commerce Development</h2>
              <p className="text-[#D4AF37] text-lg font-medium mb-6">
                Online stores that deliver seamless shopping experiences.
              </p>
              <p className="body-large mb-8">
                We build e-commerce websites that make buying easy. From product catalogs to 
                secure checkout, we create online stores that look beautiful and function flawlessly 
                across all devices.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                <div className="flex items-center gap-3">
                  <ShoppingCart className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Intuitive shopping cart</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Secure payment processing</span>
                </div>
                <div className="flex items-center gap-3">
                  <Database className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Inventory management</span>
                </div>
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Mobile shopping ready</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Streamlined checkout experience</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Product filtering & search</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Easy-to-manage product catalog</span>
                </div>
              </div>

              <div className="accent-border-left bg-[#111111] p-6">
                <p className="text-[#71717A] text-sm uppercase tracking-wider mb-2">What's Included</p>
                <ul className="text-[#A1A1AA] text-sm space-y-2">
                  <li>• Custom e-commerce design</li>
                  <li>• Product catalog setup</li>
                  <li>• Payment gateway integration</li>
                  <li>• Shipping configuration</li>
                  <li>• Order management system</li>
                  <li>• Training & documentation</li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              <div className="aspect-video bg-[#111111] border border-white/10 relative overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1698128255707-12ea168347a9?q=80&w=2340&auto=format&fit=crop"
                  alt="E-Commerce Development"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-60" />
              </div>
              
              <div className="bg-[#111111] border border-white/10 p-6">
                <h4 className="text-[#F5F5F7] font-semibold mb-4">Platforms We Work With:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <ShoppingCart className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Shopify</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Globe className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">WooCommerce</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Code className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Custom Build</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Database className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Headless CMS</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UI/UX Design */}
      <section 
        id="ui-ux"
        data-testid="service-section-ui-ux"
        className="section-padding bg-[#0a0a0a] relative"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-6xl font-bold text-[#D4AF37]/20">04</span>
                <Palette className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h2 className="heading-section text-[#F5F5F7] mb-4">UI/UX Design</h2>
              <p className="text-[#D4AF37] text-lg font-medium mb-6">
                User-centered design that balances beauty and function.
              </p>
              <p className="body-large mb-8">
                Great design is more than aesthetics. We create intuitive interfaces that guide 
                users naturally, reduce friction, and make every interaction feel effortless. 
                Form meets function in every pixel.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                <div className="flex items-center gap-3">
                  <Layers className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Wireframing & prototyping</span>
                </div>
                <div className="flex items-center gap-3">
                  <Palette className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Visual design systems</span>
                </div>
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">User research & testing</span>
                </div>
                <div className="flex items-center gap-3">
                  <MousePointer className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Interaction design</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Intuitive navigation & user flows</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Consistent brand experience</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Accessibility compliance</span>
                </div>
              </div>

              <div className="accent-border-left bg-[#111111] p-6">
                <p className="text-[#71717A] text-sm uppercase tracking-wider mb-2">What's Included</p>
                <ul className="text-[#A1A1AA] text-sm space-y-2">
                  <li>• User research & analysis</li>
                  <li>• Wireframes & user flows</li>
                  <li>• High-fidelity mockups</li>
                  <li>• Interactive prototypes</li>
                  <li>• Design system & style guide</li>
                  <li>• Developer handoff assets</li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              <div className="aspect-video bg-[#111111] border border-white/10 relative overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1618761714954-0b8cd0026356?q=80&w=2340&auto=format&fit=crop"
                  alt="UI/UX Design"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-60" />
              </div>
              
              <div className="bg-[#111111] border border-white/10 p-6">
                <h4 className="text-[#F5F5F7] font-semibold mb-4">Design Tools:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Palette className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Figma</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Layers className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Adobe XD</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Eye className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Sketch</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <MousePointer className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Prototyping</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Website Optimization */}
      <section 
        id="optimization"
        data-testid="service-section-optimization"
        className="section-padding bg-[#050505] relative"
      >
        <div className="absolute inset-0 bg-gradient-to-l from-[#D4AF37]/5 to-transparent" />
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-6xl font-bold text-[#D4AF37]/20">05</span>
                <Gauge className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h2 className="heading-section text-[#F5F5F7] mb-4">Website Optimization & Performance</h2>
              <p className="text-[#D4AF37] text-lg font-medium mb-6">
                Make your existing website faster and more effective.
              </p>
              <p className="body-large mb-8">
                Speed matters. We optimize your existing website for peak performance, improving 
                load times, user experience, and conversion rates. Every millisecond counts when 
                it comes to keeping visitors engaged.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Speed optimization</span>
                </div>
                <div className="flex items-center gap-3">
                  <Code className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Code optimization</span>
                </div>
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Mobile performance</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Core Web Vitals</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Faster page load times</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Improved user experience</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Higher conversion rates</span>
                </div>
              </div>

              <div className="accent-border-left bg-[#111111] p-6">
                <p className="text-[#71717A] text-sm uppercase tracking-wider mb-2">What's Included</p>
                <ul className="text-[#A1A1AA] text-sm space-y-2">
                  <li>• Performance audit & analysis</li>
                  <li>• Image optimization</li>
                  <li>• Code minification & compression</li>
                  <li>• Caching implementation</li>
                  <li>• Core Web Vitals optimization</li>
                  <li>• Before/after performance report</li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              <div className="aspect-video bg-[#111111] border border-white/10 relative overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=2340&auto=format&fit=crop"
                  alt="Website Optimization"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-60" />
              </div>
              
              <div className="bg-[#111111] border border-white/10 p-6">
                <h4 className="text-[#F5F5F7] font-semibold mb-4">Optimization Focus:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Zap className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Speed</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Smartphone className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Mobile</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Eye className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">UX</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Gauge className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Performance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Website Deployment & Maintenance */}
      <section 
        id="deployment"
        data-testid="service-section-deployment"
        className="section-padding bg-[#0a0a0a] relative"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-6xl font-bold text-[#D4AF37]/20">06</span>
                <Server className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h2 className="heading-section text-[#F5F5F7] mb-4">Website Deployment & Maintenance</h2>
              <p className="text-[#D4AF37] text-lg font-medium mb-6">
                Reliable deployment and ongoing support to keep your website running smoothly.
              </p>
              <p className="body-large mb-8">
                Launching your website is just the beginning. We handle deployment, hosting setup, and ongoing 
                maintenance to ensure your website stays fast, secure, and always available. From updates to 
                performance monitoring, we make sure your digital presence runs without interruption.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                <div className="flex items-center gap-3">
                  <CloudCog className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Seamless deployment process</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Secure hosting configuration</span>
                </div>
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Continuous monitoring & uptime</span>
                </div>
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A1A1AA] text-sm">Regular updates & maintenance</span>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Zero-downtime deployment practices</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Reliable performance and stability</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#F5F5F7]">Peace of mind with ongoing support</span>
                </div>
              </div>

              <div className="accent-border-left bg-[#111111] p-6">
                <p className="text-[#71717A] text-sm uppercase tracking-wider mb-2">What's Included</p>
                <ul className="text-[#A1A1AA] text-sm space-y-2">
                  <li>• Website deployment & go-live setup</li>
                  <li>• Hosting & server configuration</li>
                  <li>• Domain & DNS setup</li>
                  <li>• Regular updates & maintenance</li>
                  <li>• Performance monitoring</li>
                  <li>• Backup & recovery setup</li>
                  <li>• Ongoing technical support</li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              <div className="aspect-video bg-[#111111] border border-white/10 relative overflow-hidden group">
                <img 
                  src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2344&auto=format&fit=crop"
                  alt="Website Deployment & Maintenance"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-60" />
              </div>
              
              <div className="bg-[#111111] border border-white/10 p-6">
                <h4 className="text-[#F5F5F7] font-semibold mb-4">Coverage Includes:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Server className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Hosting Setup</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Shield className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Security Updates</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Database className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Backups</p>
                  </div>
                  <div className="text-center p-3 bg-[#0a0a0a] border border-white/5">
                    <Activity className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-xs text-[#71717A]">Uptime Monitoring</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0a0a0a]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="overline">Ready to Start?</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4 mb-6">
              Let's Build Your Website
            </h2>
            <p className="body-large mb-10">
              Every project is unique. Let's discuss which services 
              will best meet your specific needs and goals.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="btn-primary inline-flex"
            >
              Start Your Project
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
